/**
 * Login route
 *
 * Public route for user authentication.
 * Supports two modes controlled by `?mode=` search param:
 *   - "signin" (default) — email OTP sign-in for existing users
 *   - "register" — email + invite code for new users
 *
 * Microsoft OAuth is shown only when `VITE_AUTH_MICROSOFT_ENABLED=true`.
 *
 * Anti-enumeration: the sign-in flow always shows a generic "check your
 * inbox" success message regardless of server-side outcome.
 * The register flow will surface invalid invite code errors (this is
 * acceptable — it reveals code validity, not email existence).
 *
 * Accessibility (WCAG 2.2 A/AA):
 *   - 4.1.3  Status messages use `role="status"` / `role="alert"` live regions.
 *   - 3.3.1  Validation errors are programmatically associated via `aria-describedby`.
 *   - 3.3.2  Helper text is linked to fields via `aria-describedby`.
 *   - 3.3.3  Error messages are human-readable; machine codes never surface.
 *   - 1.3.5  `autocomplete` hints on email and OTP fields.
 *   - 4.1.2  Mode toggle exposes pressed state accessibly.
 *   - 3.3.8  No cognitive-function test barriers.
 */
import { useState, useCallback, useId, type FormEvent } from "react";
import { createFileRoute, Navigate, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Mail, CheckCircle2, AlertCircle } from "lucide-react";
import { useAuth } from "@/providers/auth-provider";
import { AUTH_MICROSOFT_ENABLED } from "@/env";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

// ---------------------------------------------------------------------------
// Search-param types
// ---------------------------------------------------------------------------

export type AuthMode = "signin" | "register";

export interface LoginSearchParams {
  mode?: AuthMode;
}

/**
 * Validate the `?mode=` query param.
 * Falls back to "signin" for any unrecognised value.
 */
function validateLoginSearch(
  search: Record<string, unknown>
): LoginSearchParams {
  const raw = search.mode;
  if (raw === "register") return { mode: "register" };
  return {};
}

// ---------------------------------------------------------------------------
// Error humanisation — prevents raw machine codes from reaching users
// (WCAG 3.3.3 — Error Suggestion)
// ---------------------------------------------------------------------------

/** Known machine-error codes that have dedicated i18n keys. */
const KNOWN_AUTH_ERROR_CODES = new Set([
  "INVALID_INVITE_CODE",
  "INVALID_OTP",
  "TOO_MANY_ATTEMPTS",
]);

/**
 * Ensure error messages shown to users are always human-readable.
 *
 * If the error is a known code, the caller has already mapped it to
 * an i18n string, so this is a safety net for unknown / unmapped codes
 * that might leak from the auth provider.
 */
function humanizeAuthError(
  error: string | null | undefined,
  fallback: string,
): string | null {
  if (!error) return null;
  // If the error looks like a machine code (ALL_CAPS_SNAKE), replace it.
  if (/^[A-Z][A-Z0-9_]+$/.test(error) && !KNOWN_AUTH_ERROR_CODES.has(error)) {
    return fallback;
  }
  return error;
}

// ---------------------------------------------------------------------------
// Microsoft icon (inline SVG to avoid external dependency)
// ---------------------------------------------------------------------------

function MicrosoftIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      xmlns="http://www.w3.org/2000/svg"
      width="21"
      height="21"
      viewBox="0 0 21 21"
      aria-hidden="true"
    >
      <rect x="1" y="1" width="9" height="9" fill="#f25022" />
      <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
      <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
      <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

function LoginPage() {
  const {
    isAuthenticated,
    isLoading,
    signInWithMicrosoft,
    sendEmailOtpSignIn,
    signInWithEmailOtp,
    sendMagicLinkRegister,
  } = useAuth();
  const { t } = useTranslation("common");
  const navigate = useNavigate();
  const { mode: rawMode } = Route.useSearch();

  const mode: AuthMode = rawMode === "register" ? "register" : "signin";

  // Unique id prefix for aria-describedby associations
  const idPrefix = useId();

  // Form state
  const [email, setEmail] = useState("");
  const [inviteCode, setInviteCode] = useState("");
  const [otp, setOtp] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  // Switch between sign-in / register modes
  const switchMode = useCallback(
    (next: AuthMode) => {
      setSubmittedEmail(null);
      setOtp("");
      setFormError(null);
      void navigate({
        to: "/login",
        search: next === "register" ? { mode: "register" } : {},
        replace: true,
      });
    },
    [navigate]
  );

  // Handle email-auth form submission
  const handleSubmit = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      if (!email.trim()) return;

      setSubmitting(true);
      // NOTE: we intentionally do NOT clear formError here.
      // Clearing it would hide the error banner, shrink the card, and
      // then re-show the banner when the server responds with an error —
      // causing a visible height flicker. Instead the error is cleared
      // when the user edits a field or switches modes.
      const normalizedEmail = email.trim();

      try {
        if (mode === "register") {
          const result = await sendMagicLinkRegister(normalizedEmail, inviteCode.trim());
          if (!result.ok) {
            // Show error for invalid invite code
            if (result.error === "INVALID_INVITE_CODE") {
              setFormError(t("auth.invalidInviteCode"));
            } else {
              setFormError(humanizeAuthError(result.error, t("auth.genericError")) ?? t("auth.genericError"));
            }
            return;
          }
        } else {
          await sendEmailOtpSignIn(normalizedEmail);
        }
        // Only move to OTP step if we didn't return early with an error.
        setSubmittedEmail(normalizedEmail);
      } finally {
        setSubmitting(false);
      }
    },
    [email, inviteCode, mode, sendEmailOtpSignIn, sendMagicLinkRegister, t]
  );

  const handleVerifyOtp = useCallback(async () => {
    if (!submittedEmail || !otp.trim()) return;

    setSubmitting(true);
    setFormError(null);
    try {
      const result = await signInWithEmailOtp(submittedEmail, otp.trim());
      if (!result.ok) {
        if (result.error === "INVALID_OTP") {
          setFormError(t("auth.otpInvalidOrExpired"));
        } else if (result.error === "TOO_MANY_ATTEMPTS") {
          setFormError(t("auth.otpTooManyAttempts"));
        } else {
          setFormError(humanizeAuthError(result.error, t("auth.genericError")) ?? t("auth.genericError"));
        }
        return;
      }

      void navigate({ to: "/" });
    } finally {
      setSubmitting(false);
    }
  }, [navigate, otp, signInWithEmailOtp, submittedEmail, t]);

  // ------ Guards -----------------------------------------------------------

  if (isAuthenticated) {
    return <Navigate to="/" />;
  }

  if (isLoading) {
    return (
      <div className="flex min-h-[100dvh] items-center justify-center">
        <p className="text-muted-foreground">{t("loading")}</p>
      </div>
    );
  }

  // ------ Render -----------------------------------------------------------

  // Derived ids for aria-describedby associations
  const emailErrorId = `${idPrefix}-email-error`;
  const otpErrorId = `${idPrefix}-otp-error`;
  const inviteHintId = `${idPrefix}-invite-hint`;
  const inviteErrorId = `${idPrefix}-invite-error`;

  return (
    <section className="flex min-h-[100dvh] items-center justify-center px-4 py-12">
      <div className="w-full max-w-sm space-y-4">
        <Card>
          <CardHeader className="text-center">
            <h1 className="text-2xl font-semibold leading-tight tracking-tight">
              {t("auth.signInTitle")}
            </h1>
            <CardDescription>{t("auth.signInSubtitle")}</CardDescription>
          </CardHeader>

          <CardContent className="space-y-5">
            {/* ---- Microsoft OAuth (hidden when disabled) ------------------- */}
            {AUTH_MICROSOFT_ENABLED && (
              <>
                <Button
                  variant="outline"
                  size="lg"
                  className="w-full gap-2.5"
                  onClick={() => void signInWithMicrosoft()}
                >
                  <MicrosoftIcon className="shrink-0" />
                  {t("auth.signInWithMicrosoft")}
                </Button>

                {/* ---- Divider ---------------------------------------------- */}
                <div className="relative flex items-center gap-3">
                  <span className="h-px flex-1 bg-border" />
                  <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground select-none">
                    {t("auth.or")}
                  </span>
                  <span className="h-px flex-1 bg-border" />
                </div>
              </>
            )}

             {/* ---- Mode toggle (WCAG 4.1.2 — exposes pressed state) ------ */}
            <fieldset className="flex rounded-lg bg-muted p-1">
              <legend className="sr-only">{t("auth.signInTitle")}</legend>
              <button
                type="button"
                aria-pressed={mode === "signin"}
                onClick={() => switchMode("signin")}
                className={`flex-1 rounded-md px-3 py-1.5 text-sm font-medium transition-colors duration-150 ${
                  mode === "signin"
                    ? "bg-background text-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {t("auth.modeSignIn")}
              </button>
              <button
                type="button"
                aria-pressed={mode === "register"}
                onClick={() => switchMode("register")}
                className={`flex-1 rounded-md px-3 py-1.5 text-sm font-medium transition-colors duration-150 ${
                  mode === "register"
                    ? "bg-background text-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {t("auth.modeCreateAccount")}
              </button>
            </fieldset>

            {mode === "register" && (
              <div className="flex items-start gap-2 rounded-lg border border-amber-300/60 bg-amber-50 px-3 py-2.5 text-sm text-amber-900 dark:border-amber-700/60 dark:bg-amber-950/40 dark:text-amber-100">
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
                <p>{t("auth.betaInviteOnlyDisclaimer")}</p>
              </div>
            )}

            {/* ---- Email sent + OTP verification ------------------------- */}
            {submittedEmail ? (
              <div className="space-y-4">
                {/* Success status — announced by screen readers (WCAG 4.1.3) */}
                <div
                  className="flex flex-col items-center gap-3 rounded-lg border border-primary/20 bg-primary/5 px-4 py-6 text-center"
                  aria-live="polite"
                  aria-atomic="true"
                >
                  <CheckCircle2 className="h-8 w-8 text-primary" aria-hidden="true" />
                  <div className="space-y-1">
                    <p className="text-sm font-medium">{t("auth.magicLinkSentTitle")}</p>
                    <p className="text-xs text-muted-foreground">{t("auth.otpSentDescription")}</p>
                    <p className="text-xs text-muted-foreground">{t("auth.otpSentHint")}</p>
                    <p className="text-xs text-muted-foreground">{t("auth.emailAuthExpiresHint")}</p>
                  </div>
                </div>

                {/* Error alert — announced by screen readers (WCAG 4.1.3) */}
                {formError && (
                  <div
                    id={otpErrorId}
                    role="alert"
                    className="flex items-center gap-2 rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2.5 text-sm text-destructive"
                  >
                    <AlertCircle className="h-4 w-4 shrink-0" aria-hidden="true" />
                    <span>{formError}</span>
                  </div>
                )}

                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    void handleVerifyOtp();
                  }}
                  className="space-y-4"
                >
                  <div className="space-y-2">
                    <Label htmlFor="login-otp">{t("auth.otpCodeLabel")}</Label>
                    <Input
                      id="login-otp"
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      autoComplete="one-time-code"
                      maxLength={8}
                      placeholder={t("auth.otpCodePlaceholder")}
                      required
                      aria-invalid={formError ? true : undefined}
                      aria-describedby={formError ? otpErrorId : undefined}
                      value={otp}
                      onChange={(e) => {
                        setOtp(e.target.value.replace(/\D/g, ""));
                        setFormError(null);
                      }}
                    />
                  </div>

                  <Button type="submit" className="w-full" disabled={submitting || otp.trim().length === 0}>
                    {submitting ? t("auth.verifyingCode") : t("auth.verifyCodeButton")}
                  </Button>
                </form>

                <div className="flex items-center justify-between gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      void sendEmailOtpSignIn(submittedEmail);
                    }}
                  >
                    {t("auth.tryAgain")}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setSubmittedEmail(null);
                      setOtp("");
                      setFormError(null);
                    }}
                  >
                    {t("auth.useDifferentEmail")}
                  </Button>
                </div>
              </div>
            ) : (
              /* ---- Email form ------------------------------------------- */
              <form onSubmit={(e) => void handleSubmit(e)} className="space-y-4">
                {/* Error banner — announced by screen readers (WCAG 4.1.3) */}
                {formError && (
                  <div
                    id={emailErrorId}
                    role="alert"
                    className="flex items-center gap-2 rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2.5 text-sm text-destructive"
                  >
                    <AlertCircle className="h-4 w-4 shrink-0" aria-hidden="true" />
                    <span>{formError}</span>
                  </div>
                )}

                {/* Email (WCAG 1.3.5 — autocomplete="email") */}
                <div className="space-y-2">
                  <Label htmlFor="login-email">{t("auth.emailLabel")}</Label>
                  <Input
                    id="login-email"
                    type="email"
                    placeholder={t("auth.emailPlaceholder")}
                    required
                    autoComplete="email"
                    aria-invalid={formError && mode === "signin" ? true : undefined}
                    aria-describedby={formError && mode === "signin" ? emailErrorId : undefined}
                    value={email}
                    onChange={(e) => { setEmail(e.target.value); setFormError(null); }}
                  />
                </div>

                {/* Invite code — only in register mode */}
                {mode === "register" && (
                  <div className="space-y-2">
                    <Label htmlFor="login-invite">{t("auth.inviteCodeLabel")}</Label>
                    <Input
                      id="login-invite"
                      type="text"
                      placeholder={t("auth.inviteCodePlaceholder")}
                      required
                      autoComplete="off"
                      aria-invalid={formError ? true : undefined}
                      aria-describedby={[
                        inviteHintId,
                        formError ? inviteErrorId : null,
                      ].filter(Boolean).join(" ") || undefined}
                      value={inviteCode}
                      onChange={(e) => { setInviteCode(e.target.value); setFormError(null); }}
                    />
                    <p id={inviteHintId} className="text-xs text-muted-foreground">
                      {t("auth.inviteCodeHint")}
                    </p>
                    {/* Hidden error anchor for aria-describedby when the banner is the error source */}
                    {formError && <span id={inviteErrorId} className="sr-only">{formError}</span>}
                  </div>
                )}

                {/* Submit */}
                <Button type="submit" className="w-full gap-2" disabled={submitting}>
                  <Mail className="h-4 w-4" aria-hidden="true" />
                  {submitting
                    ? t("auth.sending")
                    : mode === "register"
                      ? t("auth.createAccountButton")
                      : t("auth.sendLoginCodeButton")}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>

        {/* Legal links */}
        <div className="flex items-center justify-center gap-3 text-xs text-muted-foreground">
          <a href="/privacy" className="hover:text-foreground hover:underline underline-offset-4 transition-colors">
            {t("legal.privacyPolicy")}
          </a>
          <span aria-hidden="true">·</span>
          <a href="/terms" className="hover:text-foreground hover:underline underline-offset-4 transition-colors">
            {t("legal.termsOfService")}
          </a>
        </div>
      </div>
    </section>
  );
}

export const Route = createFileRoute("/login")({
  component: LoginPage,
  validateSearch: validateLoginSearch,
});
